import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Chip,
  TablePagination,
  TextField,
  Link,
  Tab,
  Tabs,
  useTheme,
  useMediaQuery,
  Container,
  Stack,
  InputAdornment,
  Tooltip,
  Divider
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../config/permissions';
import { useTheme as useCustomTheme } from '../ThemeContext';

export default function ProjectManagement() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [projects, setProjects] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState('active');
  const theme = useTheme();
  const { theme: customTheme } = useCustomTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const canEditProject = hasPermission(PERMISSIONS.EDIT_PROJECT);
  const canDeleteProject = hasPermission(PERMISSIONS.DELETE_PROJECT);

  useEffect(() => {
    fetchProjects();
  }, [page, rowsPerPage, search, activeTab]);

  const fetchProjects = async () => {
    try {
      let query = supabase
        .from('projects')
        .select('*', { count: 'exact' });

      if (search) {
        query = query.ilike('name', `%${search}%`);
      }
      
      // Filter based on active tab
      if (activeTab === 'active') {
        query = query.neq('status', 'archived');
      } else {
        query = query.eq('status', 'archived');
      }

      const { data, count, error } = await query
        .range(page * rowsPerPage, (page + 1) * rowsPerPage - 1)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setProjects(data || []);
      setTotalCount(count || 0);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Weet je zeker dat je dit project wilt verwijderen?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', projectId);

      if (error) throw error;
      fetchProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  const handleArchiveProject = async (projectId, currentStatus) => {
    const newStatus = currentStatus === 'archived' ? 'active' : 'archived';
    try {
      const { error } = await supabase
        .from('projects')
        .update({ status: newStatus })
        .eq('id', projectId);

      if (error) throw error;
      fetchProjects();
    } catch (error) {
      console.error('Error updating project status:', error);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header Section */}
      <Paper 
        elevation={0}
        sx={{ 
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'primary.contrastText',
          py: 4,
          px: 3,
          mb: 3
        }}
      >
        <Container maxWidth="xl">
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Projectbeheer
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Beheer en organiseer al je projecten
          </Typography>
        </Container>
      </Paper>

      <Container maxWidth="xl">
        {/* Search and Actions Section */}
        <Paper 
          elevation={0}
          sx={{ 
            p: 2, 
            mb: 3,
            border: 1,
            borderColor: 'divider',
            borderRadius: 2
          }}
        >
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={2} 
            alignItems={{ xs: 'stretch', sm: 'center' }}
            divider={<Divider orientation={isMobile ? 'horizontal' : 'vertical'} flexItem />}
          >
      <TextField
        fullWidth
              size="small"
              placeholder="Zoeken op projectnaam..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ 
                flexGrow: 1,
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.paper'
                }
              }}
            />
            
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate('/projects/new')}
              startIcon={<AddIcon />}
              sx={{ 
                minWidth: { sm: 'auto', xs: '100%' },
                whiteSpace: 'nowrap'
              }}
            >
              Nieuw Project
            </Button>
          </Stack>
        </Paper>

        {/* Tabs Section */}
        <Paper 
          elevation={0}
          sx={{ 
            mb: 3,
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
      <Tabs
        value={activeTab}
        onChange={(e, newValue) => {
          setActiveTab(newValue);
          setPage(0);
        }}
            sx={{ 
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper'
            }}
      >
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <UnarchiveIcon fontSize="small" />
                  <Typography>Actieve Projecten</Typography>
                </Box>
              } 
              value="active" 
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ArchiveIcon fontSize="small" />
                  <Typography>Gearchiveerde Projecten</Typography>
                </Box>
              } 
              value="archived" 
            />
      </Tabs>
        </Paper>

        {/* Table Section */}
        <TableContainer 
          component={Paper} 
          elevation={0}
          sx={{ 
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
        <Table>
          <TableHead>
            <TableRow>
                <TableCell sx={{ 
                  fontWeight: 600,
                  bgcolor: 'background.paper',
                  borderBottom: 2,
                  borderColor: 'divider'
                }}>Projectnaam</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600,
                  bgcolor: 'background.paper',
                  borderBottom: 2,
                  borderColor: 'divider'
                }}>Aangemaakt op</TableCell>
                <TableCell sx={{ 
                  fontWeight: 600,
                  bgcolor: 'background.paper',
                  borderBottom: 2,
                  borderColor: 'divider'
                }}>Status</TableCell>
                <TableCell align="right" sx={{ 
                  fontWeight: 600,
                  bgcolor: 'background.paper',
                  borderBottom: 2,
                  borderColor: 'divider'
                }}>Acties</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {projects.map((project) => (
                <TableRow
                  key={project.id}
                  hover
                  sx={{ 
                    '&:last-child td, &:last-child th': { border: 0 },
                    transition: 'background-color 0.2s',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                <TableCell>
                  <Link
                    component="button"
                    variant="body1"
                    onClick={() => navigate(`/projects/${project.id}/settings`)}
                    sx={{
                      textAlign: 'left',
                      textDecoration: 'none',
                        color: 'primary.main',
                        fontWeight: 500,
                      '&:hover': {
                          textDecoration: 'underline'
                      }
                    }}
                  >
                    {project.name}
                  </Link>
                </TableCell>
                <TableCell>
                  {new Date(project.created_at).toLocaleDateString('nl-NL')}
                </TableCell>
                <TableCell>
                  <Chip
                      label={project.status === 'archived' ? 'Gearchiveerd' : 'Actief'}
                    color={project.status === 'archived' ? 'warning' : 'success'}
                      size="small"
                      sx={{ 
                        fontWeight: 500,
                        '& .MuiChip-label': { px: 1 }
                      }}
                  />
                </TableCell>
                <TableCell align="right">
                    <Stack 
                      direction="row" 
                      spacing={1} 
                      justifyContent="flex-end"
                    >
                      <Tooltip title="Bewerken">
                  <IconButton
                    onClick={() => navigate(`/projects/${project.id}/settings`)}
                          size="small"
                          sx={{ 
                            color: 'primary.main',
                            '&:hover': { bgcolor: 'primary.lighter' }
                          }}
                  >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      <Tooltip title={project.status === 'archived' ? 'Activeren' : 'Archiveren'}>
                        <IconButton
                          onClick={() => handleArchiveProject(project.id, project.status)}
                          size="small"
                          sx={{ 
                            color: project.status === 'archived' ? 'success.main' : 'warning.main',
                            '&:hover': { 
                              bgcolor: project.status === 'archived' ? 'success.lighter' : 'warning.lighter'
                            }
                          }}
                        >
                          {project.status === 'archived' ? (
                            <UnarchiveIcon fontSize="small" />
                          ) : (
                            <ArchiveIcon fontSize="small" />
                          )}
                  </IconButton>
                      </Tooltip>

                  {canDeleteProject && (
                        <Tooltip title="Verwijderen">
                    <IconButton
                      onClick={() => handleDeleteProject(project.id)}
                            size="small"
                            sx={{ 
                              color: 'error.main',
                              '&:hover': { bgcolor: 'error.lighter' }
                            }}
                    >
                            <DeleteIcon fontSize="small" />
                    </IconButton>
                        </Tooltip>
                  )}
                    </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
            labelRowsPerPage="Rijen per pagina:"
            sx={{ 
              borderTop: 1,
              borderColor: 'divider',
              '.MuiTablePagination-select': {
                bgcolor: 'background.paper'
              }
            }}
        />
      </TableContainer>
      </Container>
    </Box>
  );
}